/**
 * Voice Assistant Event Bus
 * Allows components to trigger voice assistant actions globally
 * Similar to mascotEventBus for animations
 */

class VoiceAssistantEventBus {
  constructor() {
    this.listeners = {};
  }

  /**
   * Subscribe to a voice event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    };
  }

  /**
   * Emit a voice event
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    console.log(`🔔 Voice Event: ${event}`, data);
    
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in voice event listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Remove all listeners for an event
   * @param {string} event - Event name
   */
  off(event) {
    if (this.listeners[event]) {
      delete this.listeners[event];
    }
  }

  /**
   * Remove all listeners
   */
  clear() {
    this.listeners = {};
  }
}

// Create singleton instance
export const voiceAssistantEventBus = new VoiceAssistantEventBus();

// Voice event types
export const VOICE_EVENTS = {
  // Greeting events
  PLAY_GREETING: 'play_greeting',           // Play a greeting with audio blob
  SPEAK_MESSAGE: 'speak_message',           // Speak a text message (TTS)
  
  // Status events
  HOMEWORK_UPLOADED: 'homework_uploaded',   // Homework was uploaded
  VALIDATION_STARTED: 'validation_started', // Validation started
  VALIDATION_COMPLETE: 'validation_complete', // Validation finished
  
  // Navigation events
  SCENARIO_DETECTED: 'scenario_detected',   // Scenario was detected (1, 2, or 3)
  NAVIGATE_TO_PROBLEM: 'navigate_to_problem', // Navigate to a specific problem's voice tutor page
  OPEN_DINO_HELP: 'open_dino_help',
  REDIRECT_TO_HOMEWORK_UPLOAD: 'redirect_to_homework_upload',
  WORKSPACE_UPDATE_FROM_AGENT: 'workspace_update_from_agent',
  
  // Control events
  PAUSE_LISTENING: 'pause_listening',       // Temporarily pause voice
  RESUME_LISTENING: 'resume_listening',     // Resume voice listening
  STOP_SPEAKING: 'stop_speaking',           // Stop current speech

  // Homework orchestration events
  SEND_OCR_SUMMARY: 'send_ocr_summary',     // Provide OCR extraction summary to voice agent
  SEND_VALIDATION_STATUS: 'send_validation_status', // Notify agent about validation progress/results
  AGENT_ACTION: 'agent_action',             // Agent requested an application action (validate, upload more, etc.)
  OCR_PROCESSING_STARTED: 'ocr_processing_started', // OCR processing has started (send KeepAlive)
  OCR_COMPLETE: 'ocr_complete',             // OCR processing is complete, voice agent can connect now
  ENABLE_VOICE_CONNECTION: 'enable_voice_connection', // Enable voice agent connection (after OCR or timeout)

  // Completion events
  PROBLEM_COMPLETED: 'problem_completed'
};

export default voiceAssistantEventBus;

